"use client"

import { useState } from "react"
import { FONT_SANS } from "@/lib/constants"
import type { ResidenceDayInfo } from "@/lib/maps/planningEngine"

interface ResidenceDayBlockProps {
  residenceDays: ResidenceDayInfo[]
  onSelectDay: (dayKey: string) => void
  onShowAllSlots: () => void
}

export function ResidenceDayBlock({
  residenceDays,
  onSelectDay,
  onShowAllSlots,
}: ResidenceDayBlockProps) {
  const [showOtherResDays, setShowOtherResDays] = useState(false)
  const firstResDay = residenceDays[0]

  // Les jours d'une même période forment une seule proposition : le client
  // choisit son jour dans l'intervalle plutôt que de dérouler « autres dates »
  const firstPeriodDays = residenceDays.filter(
    (rd) => rd.residenceDayId === firstResDay.residenceDayId
  )
  const otherDays = residenceDays.filter((rd) => rd.residenceDayId !== firstResDay.residenceDayId)
  const isPeriod = firstResDay.periodLabel !== null && firstPeriodDays.length > 1

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)",
        border: "1px solid #BFDBFE",
        borderRadius: 14,
        padding: "20px 22px",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <span style={{ fontSize: 24, flexShrink: 0 }}>🏢</span>
        <div style={{ flex: 1 }}>
          <p
            style={{
              fontFamily: FONT_SANS,
              fontSize: 14,
              fontWeight: 700,
              color: "#1E40AF",
              margin: "0 0 4px",
            }}
          >
            {isPeriod
              ? "Intervention dédiée à votre résidence"
              : "Journée dédiée à votre résidence"}
          </p>
          <p
            style={{
              fontFamily: FONT_SANS,
              fontSize: 14,
              color: "#1D4ED8",
              margin: "0 0 14px",
            }}
          >
            {isPeriod ? (
              <>
                Une intervention est prévue <strong>{firstResDay.periodLabel}</strong>. Choisissez
                le jour qui vous convient.
              </>
            ) : (
              <>
                Une intervention est prévue le <strong>{firstResDay.label}</strong>. Souhaitez-vous
                en profiter ?
              </>
            )}
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {isPeriod ? (
              firstPeriodDays.map((rd) => (
                <button
                  key={rd.dayKey}
                  onClick={() => onSelectDay(rd.dayKey)}
                  style={{
                    background: "#1D4ED8",
                    color: "#fff",
                    fontFamily: FONT_SANS,
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "none",
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}
                >
                  {rd.label}
                  <span
                    style={{
                      display: "block",
                      fontSize: 10,
                      fontWeight: 500,
                      opacity: 0.85,
                      textTransform: "none",
                    }}
                  >
                    {rd.startTime}–{rd.endTime}
                  </span>
                </button>
              ))
            ) : (
              <button
                onClick={() => onSelectDay(firstResDay.dayKey)}
                style={{
                  background: "#1D4ED8",
                  color: "#fff",
                  fontFamily: FONT_SANS,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "9px 16px",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Oui, réserver sur cette journée
              </button>
            )}
            {otherDays.length > 0 && !showOtherResDays && (
              <button
                onClick={() => setShowOtherResDays(true)}
                style={{
                  background: "rgba(29,78,216,0.08)",
                  color: "#1D4ED8",
                  fontFamily: FONT_SANS,
                  fontSize: 13,
                  fontWeight: 600,
                  padding: "9px 16px",
                  borderRadius: 8,
                  border: "1px solid rgba(29,78,216,0.2)",
                  cursor: "pointer",
                }}
              >
                Voir les autres dates
              </button>
            )}
            <button
              onClick={onShowAllSlots}
              style={{
                background: "transparent",
                color: "var(--ljs-text-dim)",
                fontFamily: FONT_SANS,
                fontSize: 13,
                fontWeight: 500,
                padding: "9px 16px",
                borderRadius: 8,
                border: "1px solid #CBD5E1",
                cursor: "pointer",
              }}
            >
              Non, choisir une autre date
            </button>
          </div>

          {/* Autres journées résidence */}
          {showOtherResDays && otherDays.length > 0 && (
            <div
              style={{
                marginTop: 12,
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              {otherDays.map((rd) => (
                <button
                  key={rd.dayKey}
                  onClick={() => onSelectDay(rd.dayKey)}
                  style={{
                    background: "rgba(29,78,216,0.06)",
                    color: "#1D4ED8",
                    fontFamily: FONT_SANS,
                    fontSize: 13,
                    fontWeight: 600,
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: "1px solid rgba(29,78,216,0.15)",
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}
                >
                  {rd.label}
                </button>
              ))}
              <button
                onClick={onShowAllSlots}
                style={{
                  background: "transparent",
                  color: "var(--ljs-text-dim)",
                  fontFamily: FONT_SANS,
                  fontSize: 13,
                  fontWeight: 500,
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #CBD5E1",
                  cursor: "pointer",
                }}
              >
                Aucune ne me convient
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
